/**
 * Money, mirroring `reminded.domain.money` in Python.
 *
 * All arithmetic is in integer minor units. Floats are never used for money:
 * 0.1 + 0.2 !== 0.3, and a subscription total that is a penny out looks broken.
 * Division happens once, at the display edge.
 */

import type { BillingCycle, Currency, ItemRecord } from './types'
import { CURRENCY_SYMBOLS } from './types'

/** A one-off payment must not inflate recurring spend, hence 0. */
const PERIODS_PER_YEAR: Record<BillingCycle, number> = {
  one_off: 0,
  monthly: 12,
  quarterly: 4,
  annual: 1,
}

/** Annual cost in minor units. Multiplication only, so this is exact. */
export function annualisedMinor(item: Pick<ItemRecord, 'cost_minor' | 'billing_cycle'>): number {
  return item.cost_minor * PERIODS_PER_YEAR[item.billing_cycle]
}

/** Monthly cost in minor units, rounded half-up at the last step. */
export function monthlyMinor(item: Pick<ItemRecord, 'cost_minor' | 'billing_cycle'>): number {
  return roundDiv(annualisedMinor(item), 12)
}

/** Integer division rounding half away from zero, matching Python's ROUND_HALF_UP. */
export function roundDiv(numerator: number, denominator: number): number {
  const sign = numerator < 0 ? -1 : 1
  const n = Math.abs(numerator)
  return sign * Math.floor((n * 2 + denominator) / (denominator * 2))
}

/** Render minor units for display, e.g. 1099 GBP → "£10.99". */
export function formatMinor(minor: number, currency: Currency): string {
  const symbol = CURRENCY_SYMBOLS[currency]
  const negative = minor < 0
  const abs = Math.abs(minor)
  const major = Math.floor(abs / 100)
  const cents = String(abs % 100).padStart(2, '0')
  const grouped = major.toLocaleString('en-GB')
  return `${negative ? '-' : ''}${symbol}${grouped}.${cents}`
}

export interface CurrencyTotal {
  currency: Currency
  monthlyMinor: number
  annualMinor: number
}

/**
 * Totals per currency for active items.
 *
 * Currencies are never summed together: adding £10 to $10 would produce a
 * number that means nothing. Live FX is deliberately out of scope.
 */
export function totalsByCurrency(items: ItemRecord[]): CurrencyTotal[] {
  const totals = new Map<Currency, number>()
  for (const item of items) {
    if (item.status !== 'active') continue
    const annual = annualisedMinor(item)
    if (annual === 0) continue
    totals.set(item.currency, (totals.get(item.currency) ?? 0) + annual)
  }
  return [...totals.entries()]
    .map(([currency, annual]) => ({
      currency,
      annualMinor: annual,
      monthlyMinor: roundDiv(annual, 12),
    }))
    .sort((a, b) => b.annualMinor - a.annualMinor)
}

/** Parse user input like "10.99" or "£10.99" into minor units. */
export function parseMajorToMinor(input: string): number | null {
  const cleaned = input.replace(/[£$€,\s]/g, '')
  if (cleaned === '') return 0
  if (!/^-?\d*(\.\d{0,2})?$/.test(cleaned)) return null
  const value = Number(cleaned)
  if (!Number.isFinite(value)) return null
  return Math.round(value * 100)
}

/** Render minor units into a bare editable value, e.g. 1099 → "10.99". */
export function minorToMajorInput(minor: number): string {
  if (minor === 0) return ''
  return (minor / 100).toFixed(2)
}
